import { call, put, select } from 'redux-saga/effects';

import { loginRopc, check2fa } from '../../auth/authClient';
import {
    loginSuccessful,
    loginFailed,
    loginOpenPinCodeModal,
    save2FAuthInfo,
    passwordTooWeak,
} from '../../redux/login/actions';
import { messagesShow } from '../../redux/messages/actions';
import { twoFAuthInfoSelector } from '../../selectors/login';
import getReCAPTCHAToken from '../getReCAPTCHAToken';

export function* twoFAloginRequest({ payload }) {
    const twoFAuthInfo = yield select(twoFAuthInfoSelector);

    const params = {
        pinCode: payload.pinCode,
        sessionId: twoFAuthInfo.sessionId,
        customUrl: twoFAuthInfo.url,
    };

    let result;
    try {
        result = yield call(check2fa, {
            url: params.customUrl,
            sessionId: params.sessionId,
            pin: params.pinCode,
        });
    } catch (error) {
        // Guard ONLY the SW call: a rejection / uninitialized client must not
        // hang the modal. The post-result dispatches stay out of the try so a
        // React render error triggered by loginSuccessful is not misread as a
        // 2FA failure (which would dispatch loginFailed over a real success).
        console.error('twoFAloginRequest', error);
        yield put(loginOpenPinCodeModal(false));
        yield put(messagesShow('internalServerError', 'error'));
        yield put(loginFailed());
        return;
    }

    if (result.ok) {
        yield put(loginSuccessful());
        return;
    }

    // The SW relays the IDP body under details.body on a rejection. No body at
    // all means the request never reached the IDP (SW timeout, no controller, no
    // config) — blaming the PIN there tells the user their correct code is wrong
    // and can walk them into an IDP lockout.
    const body = (result.details && result.details.body) || null;
    if (!body) {
        console.error('twoFAloginRequest: no IDP response', result.code, result.message);
        yield put(messagesShow('internalServerError', 'error'));
    } else if (body.invalid_reason === 'weak password') {
        yield put(passwordTooWeak(true));
    } else {
        yield put(messagesShow('invalidPinCode', 'error'));
    }
    yield put(loginOpenPinCodeModal(false));
    yield put(loginFailed());
}

function* handleOldLoginErrors(err) {
    const {
        data: { error, invalid_reason: invalidReason, session_id: sessionId, url, pin_code_format: pinCodeFormat },
    } = err;

    if (error === '2FA') {
        // Open modal to get sms pin code
        yield put(save2FAuthInfo({ sessionId, url, pinCodeFormat }));
        yield put(loginOpenPinCodeModal(true));
    } else if (invalidReason && invalidReason !== 'weak password') {
        // The user hasn't provided any phone number
        yield put(loginFailed());
        yield put(messagesShow(invalidReason, 'error'));
    } else if (invalidReason && invalidReason === 'weak password') {
        yield put(loginFailed());
        yield put(passwordTooWeak(true));
    } else if (error) {
        yield put(loginFailed());
        yield put(messagesShow(error, 'error'));
    }
}

function* handleNewLoginErrors(err, history) {
    const {
        data: { message_code: messageCode, error },
    } = err;
    let errorToTranslate = null;
    let redirectToResetPassword = false;
    let isTooWeak = false;

    if (error && messageCode && error !== '2FA') {
        if (messageCode === 'IncorrectCredentials') {
            errorToTranslate = 'errorIncorrectCredentials';
        } else if (messageCode === 'LockedUser') {
            errorToTranslate = 'errorLockedUser';
        } else if (messageCode === 'BadPasswordWeak') {
            errorToTranslate = 'errorPasswordWeak';
            redirectToResetPassword = true;
            isTooWeak = true;
        } else if (messageCode === 'BadPassword') {
            errorToTranslate = 'errorPasswordExpired';
            redirectToResetPassword = true;
        } else if (messageCode === 'TokenAlreadyUsed') {
            errorToTranslate = 'errorTokenAlreadyUsed';
        } else if (messageCode === 'DisabledUser') {
            errorToTranslate = 'errorDisabledUser';
        }

        yield put(messagesShow(errorToTranslate, 'error', 'true'));

        if (redirectToResetPassword) {
            history.push(`/send_lost_password_email${isTooWeak ? '?passwordWeak=true' : ''}`);
        }
        yield put(loginFailed());
    } else {
        yield call(handleOldLoginErrors, err);
    }
}

export function* getToken({ payload: { login, password, reCAPTCHAsiteKey, history } }) {
    let result;
    try {
        // Guard ONLY the token acquisition (reCAPTCHA can reject on script/load
        // failure; loginRopc rejects on transport error or if requireClient()
        // throws before initAuth resolves). The post-result dispatches stay OUT
        // of the try so a React render error triggered by loginSuccessful is not
        // misread as a login failure (which would dispatch loginFailed over a
        // real success and add fuel to a render loop).
        const { recaptchaToken } = yield call(getReCAPTCHAToken, 'login', reCAPTCHAsiteKey);

        result = yield call(loginRopc, {
            username: login,
            password,
            recaptchaToken,
            recaptchaSiteKey: reCAPTCHAsiteKey,
        });
    } catch (error) {
        console.error('getToken', error);
        yield put(messagesShow('internalServerError', 'error'));
        yield put(loginFailed());
        return;
    }

    if (result.ok) {
        yield put(loginSuccessful());
        return;
    }

    if (result.code === 'TWO_FA_REQUIRED') {
        yield put(
            save2FAuthInfo({
                sessionId: result.details.sessionId,
                url: result.details.url,
                pinCodeFormat: result.details.pinCodeFormat,
            })
        );
        yield put(loginOpenPinCodeModal(true));
        return;
    }

    // Rebuild the legacy error shape ({ data: <IDP body> }) from the SW
    // result so the existing error handlers keep working unchanged.
    const body = (result.details && result.details.body) || null;
    if (!body) {
        // Every branch of handleNewLoginErrors / handleOldLoginErrors keys off a
        // field of the IDP body, so an empty one dispatches nothing at all: the
        // spinner stops and the form sits there with no explanation.
        console.error('getToken: no IDP response', result.code, result.message);
        yield put(messagesShow('internalServerError', 'error'));
    } else if (body.invalid_reason === 'weak password') {
        yield put(passwordTooWeak(true));
    } else {
        yield call(handleNewLoginErrors, { data: body }, history);
    }
    yield put(loginFailed());
}
